import { Link } from "react-router-dom";
import { ArrowUpRight, Compass, Home as HomeIcon, Building2, MapPinned, Sparkles, Stars } from "lucide-react";
import { IMG } from "../lib/assets";
import Marquee from "../components/Marquee";

const services = [
  { icon: HomeIcon, title: "Home Vastu", desc: "Harmonise the five elements in your living spaces for peace, prosperity and health." },
  { icon: Building2, title: "Office Vastu", desc: "Activate wealth, clarity and leadership zones for sustained business growth." },
  { icon: MapPinned, title: "Plot Selection", desc: "Science-backed site analysis — orientation, shape, slope, and surroundings." },
  { icon: Sparkles, title: "Astro Remedies", desc: "Planet-aligned, karmic remedies — mantra, yantra, gemstone, colour therapy." },
  { icon: Stars, title: "Birth Chart + Vastu Combo", desc: "Integrated kundali & space reading — where stars and walls both align to you." },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[100vh] flex items-center overflow-hidden">
        <img
          src={IMG.heroMandala}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#FBF7F0]/70 via-[#FBF7F0]/55 to-[#FBF7F0]" />
        <div className="absolute inset-0 noise" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 w-full pt-32 pb-24">
          <div className="max-w-3xl fade-up">
            <div className="overline mb-6" data-testid="hero-overline">Mumbai · Est. Vastu Practice</div>
            <h1 className="font-bodoni text-5xl sm:text-6xl lg:text-8xl font-light tracking-tighter leading-[0.95] text-[#1E1A17]">
              Align.<br />
              <span className="italic text-[#A67C00]">Energise.</span><br />
              Manifest.
            </h1>
            <p className="mt-8 text-[#6B5F55] text-lg leading-relaxed max-w-xl">
              Tarini is a private Astro-Vastu practice by{" "}
              <span className="text-[#C8683F]">Divya Agarwal</span> — blending the 16-zone
              Vastu system with Vedic astrology to reshape your home, workplace and life.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4" data-testid="hero-cta-group">
              <Link
                to="/vastu"
                data-testid="hero-cta-explore"
                className="group inline-flex items-center gap-2 bg-[#C9A227] text-white px-7 py-3.5 rounded-sm text-sm font-medium hover:bg-[#C8683F] transition-colors"
              >
                Explore Vastu
                <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link
                to="/about"
                data-testid="hero-cta-meet-divya"
                className="inline-flex items-center gap-2 border border-[#C9A227]/50 text-[#A67C00] px-7 py-3.5 rounded-sm text-sm font-medium hover:bg-[#C9A227]/10 transition-colors"
              >
                Meet Divya
              </Link>
            </div>

            <div className="mt-16 flex flex-wrap gap-10 text-sm text-[#6B5F55]">
              <div><span className="font-bodoni text-3xl text-[#A67C00] block">5+</span>Years of guidance</div>
              <div><span className="font-bodoni text-3xl text-[#A67C00] block">100+</span>Homes transformed</div>
              <div><span className="font-bodoni text-3xl text-[#A67C00] block">16</span>Vastu zones mastered</div>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <Marquee items={["16 Vastu Zones", "5 Elements", "Sacred Geometry", "Ancient Wisdom", "Mumbai"]} />

      {/* INTRO / PHILOSOPHY */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="overline mb-6">Our Philosophy</div>
            <h2 className="font-bodoni text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#1E1A17] leading-tight">
              Every wall, every threshold, every corner{" "}
              <span className="italic text-[#A67C00]">carries a frequency.</span>
            </h2>
            <p className="mt-8 text-[#6B5F55] leading-relaxed text-base max-w-2xl">
              Vastu is not superstition — it is a precise, directional science of energy.
              At Tarini, we read the vibrational blueprint of your space and your stars together,
              then craft remedies that feel effortless, elegant, and measurably effective.
            </p>
            <div className="mt-10 yantra-divider w-full max-w-md" />
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-8">
              {[
                { k: "01", t: "Diagnose", d: "Directional audit of your space & chart." },
                { k: "02", t: "Decode", d: "Map imbalances to life areas." },
                { k: "03", t: "Deliver", d: "Subtle, non-demolition remedies." },
              ].map((s) => (
                <div key={s.k}>
                  <div className="font-bodoni text-2xl text-[#A67C00]">{s.k}</div>
                  <div className="font-bodoni text-xl text-[#1E1A17] mt-1">{s.t}</div>
                  <div className="text-sm text-[#6B5F55] mt-2">{s.d}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="relative yantra-card overflow-hidden">
              <img src={IMG.vastuCompass} alt="Brass compass" className="w-full h-[520px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#FBF7F0]/70 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 font-bodoni text-2xl text-[#C8683F] italic">
                “The needle never lies — and neither does a well-aligned home.”
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-24 md:py-32 bg-[#F3EADB] border-y border-[#C9A227]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <div>
              <div className="overline mb-4">Our Services</div>
              <h2 className="font-bodoni text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight">
                Consultations tailored to <span className="italic text-[#A67C00]">your blueprint</span>
              </h2>
            </div>
            <Link
              to="/contact"
              data-testid="services-cta"
              className="inline-flex items-center gap-2 text-[#A67C00] hover:text-[#C8683F] text-sm tracking-wide border-b border-[#C9A227]/40 pb-1"
            >
              Book a private session <ArrowUpRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
            {services.map((s, i) => (
              <div
                key={s.title}
                data-testid={`service-card-${i}`}
                className={`yantra-card p-8 bg-white ${
                  i === 0 ? "md:col-span-4" : i === 1 ? "md:col-span-2" : "md:col-span-2"
                }`}
              >
                <s.icon className="text-[#A67C00]" size={26} />
                <div className="font-bodoni text-2xl text-[#1E1A17] mt-6">{s.title}</div>
                <p className="text-sm text-[#6B5F55] mt-3 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VASTU TEASER */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <img src={IMG.brassDiya} alt="Brass diya" className="w-full h-[500px] object-cover yantra-card" />
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2">
            <div className="overline mb-5">The Science</div>
            <h2 className="font-bodoni text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight leading-tight">
              Four principles. Sixteen zones. <span className="italic text-[#A67C00]">Infinite alignment.</span>
            </h2>
            <p className="mt-6 text-[#6B5F55] leading-relaxed max-w-2xl">
              The Vastu system divides every built space into 16 directional zones —
              each governed by a planet, an element, and a life area. A missing or overstimulated
              zone quietly shapes how money, relationships and health flow through your life.
            </p>
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {["NE · Clarity", "SE · Wealth", "SW · Stability", "NW · Support"].map((z) => (
                <div key={z} className="b-gold p-4 text-center text-sm text-[#C8683F] tracking-wide">{z}</div>
              ))}
            </div>
            <Link
              to="/vastu"
              data-testid="vastu-teaser-cta"
              className="mt-10 inline-flex items-center gap-2 text-[#A67C00] hover:text-[#C8683F] text-sm tracking-wide border-b border-[#C9A227]/40 pb-1"
            >
              Study all 16 zones <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="relative py-28 md:py-36 border-t border-[#C9A227]/15">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Compass className="mx-auto text-[#A67C00]" size={36} />
          <h2 className="font-bodoni text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter mt-8 text-[#1E1A17]">
            Begin a <span className="italic text-[#A67C00]">quiet shift</span> in your life.
          </h2>
          <p className="mt-6 text-[#6B5F55] max-w-xl mx-auto">
            A private consultation with Divya — in person in Mumbai, or over a secure video call anywhere in the world.
          </p>
          <Link
            to="/contact"
            data-testid="home-final-cta"
            className="mt-10 inline-flex items-center gap-2 bg-[#C9A227] text-white px-8 py-3.5 rounded-sm text-sm font-medium hover:bg-[#C8683F]"
          >
            Request Consultation <ArrowUpRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}
